import { apiCall } from '../../../Middleware/API/apiRequest';
import { getTokensFromStorage } from '../../../Utils/Auth/AuthToken';

const { accessToken } = getTokensFromStorage();
const routeName = '/webhook/logs';
const SUPPORTED_WEBHOOK_LOG_FIELDS = new Set([
  'webhook_log_id',
  'author',
  'repo',
  'status',
  'commit_date',
  'created_date',
]);
const DEFAULT_WEBHOOK_LOG_SORT = [
  {
    column: 'created_date',
    order: 'desc',
  },
];
const DEFAULT_WEBHOOK_LOG_FILTER_OPERATOR = 'OR';

const getDefaultFilterOperatorForField = (columnName) => {
  if (
    columnName === 'webhook_log_id' ||
    columnName === 'status' ||
    columnName === 'commit_date' ||
    columnName === 'created_date'
  ) {
    return 'eq';
  }

  return 'contains';
};

const normalizeRequestedWebhookFilterOperator = (
  requestedOperator,
  columnName
) => {
  const normalizedOperator = String(requestedOperator ?? '')
    .trim()
    .toLowerCase();

  if (
    normalizedOperator === 'eq' ||
    normalizedOperator === 'contains' ||
    normalizedOperator === 'gte' ||
    normalizedOperator === 'lte'
  ) {
    return normalizedOperator;
  }

  return getDefaultFilterOperatorForField(columnName);
};

const flattenWebhookLogFilters = (filters) => {
  if (Array.isArray(filters)) {
    return filters.flatMap((filter) => flattenWebhookLogFilters(filter));
  }

  if (!filters || typeof filters !== 'object') {
    return [];
  }

  if (Array.isArray(filters.filters)) {
    return filters.filters.flatMap((filter) =>
      flattenWebhookLogFilters(filter)
    );
  }

  return [filters];
};

const normalizeWebhookLogFilter = (filter) => {
  const columnName = String(filter?.ColumnName ?? filter?.field ?? '').trim();

  if (!SUPPORTED_WEBHOOK_LOG_FIELDS.has(columnName)) {
    return null;
  }

  const rawValue =
    filter?.ColumnValue ?? filter?.value ?? filter?.columnValue ?? '';
  const columnValue =
    typeof rawValue === 'string' ? rawValue.trim() : (rawValue ?? '');

  if (columnValue === '' || columnValue === null || columnValue === undefined) {
    return null;
  }

  return {
    ColumnName: columnName,
    Op: normalizeRequestedWebhookFilterOperator(
      filter?.Op ?? filter?.operator,
      columnName
    ),
    ColumnValue: columnValue,
  };
};

const normalizeWebhookLogFilters = (filters) => {
  const flattenedFilters = flattenWebhookLogFilters(filters);

  if (flattenedFilters.length === 0) {
    return [];
  }

  return flattenedFilters.map(normalizeWebhookLogFilter).filter(Boolean);
};

const normalizeWebhookLogSort = (sort) => {
  if (!Array.isArray(sort) || sort.length === 0) {
    return DEFAULT_WEBHOOK_LOG_SORT;
  }

  const normalizedSort = sort
    .map((item) => {
      const column = String(item?.column ?? item?.field ?? '').trim();

      if (!SUPPORTED_WEBHOOK_LOG_FIELDS.has(column)) {
        return null;
      }

      const requestedOrder = String(item?.order ?? item?.dir ?? '')
        .trim()
        .toLowerCase();

      return {
        column,
        order: requestedOrder === 'asc' ? 'asc' : 'desc',
      };
    })
    .filter(Boolean);

  return normalizedSort.length > 0 ? normalizedSort : DEFAULT_WEBHOOK_LOG_SORT;
};

/**
 * Lists Webhook Logs.
 * @returns {Promise} - A promise that resolves with the list of Webhook or rejects with an error.
 * @param props
 */
export const listWebHookLogs = async ({
  pageSize,
  pageNumber,
  filters,
  filterOperator = DEFAULT_WEBHOOK_LOG_FILTER_OPERATOR,
  limit,
  offset,
  filter,
  sort,
  moduleName,
  isServiceWorker,
  listController,
}) => {
  const normalizedFilters = normalizeWebhookLogFilters(filters ?? filter);
  const resolvedPageSize = Number(pageSize ?? limit ?? 20) || 20;
  const resolvedPageNumber = Number(
    pageNumber ?? (offset === 0 ? 1 : offset) ?? 1
  );
  const data = {
    pageSize: resolvedPageSize,
    pageNumber: resolvedPageNumber > 0 ? resolvedPageNumber : 1,
    Filters: normalizedFilters,
    FilterOperator:
      String(filterOperator).trim().toUpperCase() === 'AND' ? 'AND' : 'OR',
    sort: normalizeWebhookLogSort(sort),
  };
  const response = await apiRequest({
    url: `${routeName}`,
    method: 'POST',
    moduleName,
    isServiceWorker,
    listController,
    body: JSON.stringify(data),
    accessToken,
  });
  if (!response?.isSuccess) {
    if (isServiceWorker) {
      let cacheResponse = await getGraphqlCacheResponse(
        headers,
        isServiceWorker
      );
      return cacheResponse;
    }
    return response;
  }
  if (isServiceWorker) {
    await setGraphqlCacheResponse(headers, response, isServiceWorker);
  }
  const payload = response?.payload;
  const normalizedPayload =
    Array.isArray(payload) || !payload ? { data: payload } : payload;

  return {
    isSuccess: response?.isSuccess,
    webhooklogs: normalizedPayload?.data,
    stats: normalizedPayload?.stats,
    pagination: normalizedPayload?.pagination,
    totalRecordCount: response?.totalRecordCount,
    status: response?.status,
    message: response?.status?.message || response?.message,
  };
};

/**
 * Lists Affected Files.
 * @returns {Promise} - A promise that resolves with the list of Affected Files or rejects with an error.
 * @param props
 */
export const getWebHookLogDetail = async ({ webHookId }) => {
  const response = await apiRequest({
    url: `/webhook/payload/${webHookId}`,
    method: 'GET',
    accessToken,
  });
  return response;
};

/**
 * Api Call Wrapper Method.
 * @param {object} api - Contains details like url headers method and data.
 * @returns {Promise} - A promise that resolves with the data or rejects with an error.
 */

/**
 * Api Call Wrapper Method.
 * @param {object} api - Contains details like url headers method and data.
 * @returns {Promise} - A promise that resolves with the data or rejects with an error.
 */
const apiRequest = async (request) => {
  let response;
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  request.headers = headers;
  try {
    response = await apiCall(request);

    if (!response.isSuccess) {
      return { isSuccess: false, errorMessage: response.message };
    }
    return response;
  } catch (e) {
    response = { isSuccess: false, message: e.message };
  }
  return response;
};
